import { useState, useEffect } from 'react';
import { Box, Grid, TextField, CircularProgress, Button, MenuItem } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormDialog from '../../../../components/FormDialog';
import PickerField from '../../../../components/PickerField';
import PickerFieldTable from '../../../../components/PickerFieldTable';
import { useNotifier } from '../../../../components/Notifier';
import { useLanguage } from '../../../../utils/i18n';

import { createTireSpec, updateTireSpec, getTireSpecById } from '../../../../stores/tireSpecStore';
import { useCompanyDataList } from '../../../../stores/companyStore';
import { useTireBrandDataList } from '../../../../stores/tireBrandStore';
import { useTireSizeDataList } from '../../../../stores/tireSizeStore';
import usePickerPagination from '../../../../hooks/usePickerPagination';
import MasterCompanyAddEdit from '../../../master-data/company/MasterCompanyAddEdit';
import MasterBrandAddEdit from '../brand/MasterBrandAddEdit';
import MasterSizeAddEdit from '../size/MasterSizeAddEdit';

const SpecificationAddEdit = ({ open, id = null, onClose, onSuccess }) => {
  const notify = useNotifier();
  const { t } = useLanguage();

  const [initialValues, setInitialValues] = useState({
    company: typeof window !== 'undefined' ? localStorage.getItem('companyId') || '' : '',
    spec_name: '',
    tire_brand_id: '',
    tire_size_id: '',
    pattern: '',
    original_tread_depth: '',
    min_tread_depth: '',
    pressure_std: '',
    load_index: '',
    tkph: '',
    expected_lifetime_km: '',
    expected_lifetime_hour: '',
    max_speed: '',
    status: 'Active'
  });

  const [loading, setLoading] = useState(false);
  const [prefilledBrand, setPrefilledBrand] = useState([]);
  const [prefilledSize, setPrefilledSize] = useState([]);

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  const brandPickerParams = usePickerPagination({
    fetchHook: useTireBrandDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  const sizePickerParams = usePickerPagination({
    fetchHook: useTireSizeDataList,
    buildArgs: (state) => [state.page, state.pageSize, [], state.searchKey, state.searchValue],
    searchKey: 'code',
    formatOption: (item) => ({
      value: String(item.id),
      label: item.code || item.code_internal || String(item.id)
    })
  });

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    const fetch = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await getTireSpecById(id);
        if (!mounted) return;
        if (data) {
          if (data.tire_brand || data.tire_brand_id) {
            setPrefilledBrand([
              {
                id: data.tire_brand ?? data.tire_brand_id,
                name:
                  data.join?.tire_brand_name ||
                  data.tire_brand_name ||
                  data.brand_name ||
                  data.join?.m_tire_brand_name ||
                  String(data.tire_brand ?? data.tire_brand_id),
                code:
                  data.join?.tire_brand_code ||
                  data.tire_brand_code ||
                  data.brand_code ||
                  data.join?.m_tire_brand_code ||
                  String(data.tire_brand ?? data.tire_brand_id)
              }
            ]);
          }
          if (data.tire_size || data.tire_size_id) {
            setPrefilledSize([
              {
                id: data.tire_size ?? data.tire_size_id,
                name:
                  data.join?.tire_size_name ||
                  data.tire_size_name ||
                  data.size_name ||
                  data.join?.m_tire_size_name ||
                  String(data.tire_size ?? data.tire_size_id),
                value: data.tire_size ?? data.tire_size_id,
                label:
                  data.join?.tire_size_name ||
                  data.tire_size_name ||
                  data.size_name ||
                  data.join?.m_tire_size_name ||
                  String(data.tire_size ?? data.tire_size_id)
              }
            ]);
          }
          setInitialValues((prev) => ({
            ...prev,
            company: data.company_id ?? data.company ?? prev.company,
            spec_name: data.spec_name ?? prev.spec_name,
            tire_brand_id: data.tire_brand ?? data.tire_brand_id ?? prev.tire_brand_id,
            tire_size_id: data.tire_size ?? data.tire_size_id ?? prev.tire_size_id,
            pattern: data.pattern ?? prev.pattern,
            original_tread_depth: data.original_tread_depth ?? data.tread_depth ?? prev.original_tread_depth,
            min_tread_depth: data.min_tread_depth ?? prev.min_tread_depth,
            pressure_std: data.pressure_std ?? prev.pressure_std,
            load_index: data.load_index ?? prev.load_index,
            tkph: data.tkph ?? data.tkph_tmph ?? prev.tkph,
            expected_lifetime_km: data.expected_lifetime_km ?? prev.expected_lifetime_km,
            expected_lifetime_hour: data.expected_lifetime_hour ?? prev.expected_lifetime_hour,
            max_speed: data.max_speed ?? prev.max_speed,
            status: data.status === 'Active' || data.status === true || data.status === 1 ? 'Active' : 'Inactive'
          }));
        }
      } catch (err) {
        console.error('Failed to load tire spec:', err);
        notify({ message: t('general.failedLoadData', 'Failed to load data'), severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => {
      mounted = false;
    };
  }, [id, open, notify, t]);

  const validationSchema = Yup.object().shape({
    company: Yup.string().required(t('general.validationCompanyRequired', 'Company is required')),
    tire_brand_id: Yup.string().required(t('general.validationBrandRequired', 'Brand is required')),
    tire_size_id: Yup.string().required(t('general.validationSizeRequired', 'Size is required')),
    pattern: Yup.string().required(t('general.validationPatternRequired', 'Pattern is required')),
    original_tread_depth: Yup.number()
      .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
      .typeError(t('general.mustBeNumber', 'Must be a number'))
      .required(t('specification.validationOtdRequired', 'OTD is required'))
      .min(5, t('specification.validationOtdMin', 'OTD must be at least 5 mm'))
      .max(200, t('specification.validationOtdMax', 'OTD must be at most 200 mm')),
    min_tread_depth: Yup.number()
      .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
      .typeError(t('general.mustBeNumber', 'Must be a number'))
      .required(t('specification.validationMinOtdRequired', 'Min Tread Depth is required')),
    pressure_std: Yup.number()
      .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
      .typeError(t('general.mustBeNumber', 'Must be a number'))
      .required(t('specification.validationPressureRequired', 'Pressure is required'))
      .min(20, t('specification.validationPressureMin', 'Pressure must be at least 20 psi'))
      .max(200, t('specification.validationPressureMax', 'Pressure must be at most 200 psi'))
  });

  const handleClose = () => {
    formik.resetForm();
    if (onClose) onClose();
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          company: Number(values.company),
          spec_name: values.spec_name ?? '',
          tire_brand: Number(values.tire_brand_id),
          tire_size: Number(values.tire_size_id),
          pattern: values.pattern ?? '',
          original_tread_depth: values.original_tread_depth === '' ? null : Number(values.original_tread_depth),
          min_tread_depth: values.min_tread_depth === '' ? null : Number(values.min_tread_depth),
          pressure_std: values.pressure_std === '' ? null : Number(values.pressure_std),
          load_index: values.load_index ?? '',
          tkph: values.tkph === '' ? null : Number(values.tkph),
          expected_lifetime_km: values.expected_lifetime_km === '' ? null : Number(values.expected_lifetime_km),
          expected_lifetime_hour: values.expected_lifetime_hour === '' ? null : Number(values.expected_lifetime_hour),
          max_speed: values.max_speed === '' ? null : Number(values.max_speed),
          status: values.status || 'Active'
        };

        if (id) {
          await updateTireSpec(id, payload);
          notify({ message: t('general.updateSuccess', 'Data updated successfully'), severity: 'success' });
        } else {
          await createTireSpec(payload);
          notify({ message: t('general.createSuccess', 'Data added successfully'), severity: 'success' });
        }
        formik.resetForm();
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.response?.data?.message || err?.message || 'Failed to save data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const dialogActions = (
    <>
      <Button onClick={handleClose} disabled={formik.isSubmitting} sx={{ color: 'text.secondary', textTransform: 'none' }}>
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={formik.handleSubmit}
        disabled={formik.isSubmitting || loading}
        startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{ textTransform: 'none', borderRadius: 1, px: 3, fontWeight: 700 }}
      >
        {formik.isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
      </Button>
    </>
  );

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      title={
        id
          ? t('general.edit', 'Edit') + ' ' + t('specification.title', 'Specification')
          : t('general.add', 'Add') + ' ' + t('specification.title', 'Specification')
      }
      maxWidth="md"
      fullScreenMode={false}
      actions={dialogActions}
    >
      <Box sx={{ width: '100%', pt: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item size={{ xs: 12, md: 6 }}>
                <PickerField
                  label={t('general.company', 'Company') + ' *'}
                  value={formik.values.company}
                  onChange={(val) => formik.setFieldValue('company', val ?? '')}
                  error={formik.touched.company && !!formik.errors.company}
                  helperText={formik.touched.company && formik.errors.company}
                  options={companyPickerParams.options}
                  loading={companyPickerParams.loading}
                  onSearch={companyPickerParams.handleSearch}
                  onScrollEnd={companyPickerParams.handleScrollEnd}
                  allowAdd
                  AddComponent={MasterCompanyAddEdit}
                  getOptionId={(o) => o?.value ?? o?.id ?? ''}
                  getOptionLabel={(o) => o?.label ?? o?.name ?? ''}
                  dialogTitle={t('general.selectCompany', 'Select Company')}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  name="spec_name"
                  label={t('specification.spec_name', 'Name')}
                  value={formik.values.spec_name}
                  onChange={formik.handleChange}
                  error={formik.touched.spec_name && !!formik.errors.spec_name}
                  helperText={formik.touched.spec_name && formik.errors.spec_name}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <PickerFieldTable
                  label={t('specification.brand', 'Brand') + ' *'}
                  value={formik.values.tire_brand_id}
                  selectedItems={prefilledBrand}
                  onChange={(val) => formik.setFieldValue('tire_brand_id', val ?? '')}
                  error={formik.touched.tire_brand_id && !!formik.errors.tire_brand_id}
                  helperText={formik.touched.tire_brand_id && formik.errors.tire_brand_id}
                  options={brandPickerParams.options}
                  listOptions={brandPickerParams.listOptions}
                  isLoading={brandPickerParams.isLoading}
                  onSearch={brandPickerParams.onSearch}
                  page={brandPickerParams.page}
                  pageSize={brandPickerParams.pageSize}
                  pageSizeOptions={brandPickerParams.pageSizeOptions}
                  onPageChange={brandPickerParams.onPageChange}
                  onPageSizeChange={brandPickerParams.onPageSizeChange}
                  totalRecord={brandPickerParams.totalRecord}
                  allowAdd
                  AddComponent={MasterBrandAddEdit}
                  getOptionId={(o) => o?.value ?? o?.id ?? ''}
                  getOptionLabel={(o) => o?.label ?? o?.name ?? ''}
                  dialogTitle={t('specification.selectBrand', 'Select Brand')}
                  columns={[
                    {
                      field: 'code',
                      headerName: t('general.code', 'Code'),
                      flex: 1,
                      renderCell: (opt) => {
                        const raw = opt?.raw || opt;
                        return raw?.code_internal || raw?.code || '-';
                      }
                    },
                    {
                      field: 'name',
                      headerName: t('general.name', 'Name'),
                      flex: 1,
                      renderCell: (opt) => {
                        const raw = opt?.raw || opt;
                        return raw?.name || '-';
                      }
                    },
                    {
                      field: 'manufacture_name',
                      headerName: t('menu.manufacture', 'Manufacturer'),
                      flex: 1,
                      renderCell: (opt) => {
                        const raw = opt?.raw || opt;
                        return raw?.join?.manufacture_name || raw?.manufacture_name || '-';
                      }
                    },
                    {
                      field: 'country_name',
                      headerName: t('workingArea.country', 'Country'),
                      flex: 1,
                      renderCell: (opt) => {
                        const raw = opt?.raw || opt;
                        return raw?.join?.country_name || raw?.country_name || '-';
                      }
                    }
                  ]}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <PickerField
                  label={t('specification.size', 'Size') + ' *'}
                  value={formik.values.tire_size_id}
                  selectedItems={prefilledSize}
                  onChange={(val) => formik.setFieldValue('tire_size_id', val ?? '')}
                  error={formik.touched.tire_size_id && !!formik.errors.tire_size_id}
                  helperText={formik.touched.tire_size_id && formik.errors.tire_size_id}
                  options={sizePickerParams.options}
                  loading={sizePickerParams.loading}
                  onSearch={sizePickerParams.handleSearch}
                  onScrollEnd={sizePickerParams.handleScrollEnd}
                  allowAdd
                  AddComponent={MasterSizeAddEdit}
                  getOptionId={(o) => o?.value ?? o?.id ?? ''}
                  getOptionLabel={(o) => o?.label ?? o?.name ?? ''}
                  dialogTitle={t('specification.selectSize', 'Select Size')}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  name="pattern"
                  label={t('specification.pattern', 'Pattern') + ' *'}
                  value={formik.values.pattern}
                  onChange={formik.handleChange}
                  error={formik.touched.pattern && !!formik.errors.pattern}
                  helperText={formik.touched.pattern && formik.errors.pattern}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  name="load_index"
                  label={t('specification.loadIndex', 'Load Index')}
                  value={formik.values.load_index}
                  onChange={formik.handleChange}
                  error={formik.touched.load_index && !!formik.errors.load_index}
                  helperText={formik.touched.load_index && formik.errors.load_index}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  name="pressure_std"
                  label={t('specification.pressureStd', 'Air Pressure Standard (psi)') + ' *'}
                  type="number"
                  value={formik.values.pressure_std}
                  onChange={formik.handleChange}
                  error={formik.touched.pressure_std && !!formik.errors.pressure_std}
                  helperText={formik.touched.pressure_std && formik.errors.pressure_std}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  name="original_tread_depth"
                  label={t('specification.originalTreadDepth', 'Original Tread Depth (mm)') + ' *'}
                  type="number"
                  value={formik.values.original_tread_depth}
                  onChange={formik.handleChange}
                  error={formik.touched.original_tread_depth && !!formik.errors.original_tread_depth}
                  helperText={formik.touched.original_tread_depth && formik.errors.original_tread_depth}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  name="tkph"
                  label={t('specification.tkphTmph', 'TKPH/TMPH')}
                  type="number"
                  value={formik.values.tkph}
                  onChange={formik.handleChange}
                  error={formik.touched.tkph && !!formik.errors.tkph}
                  helperText={formik.touched.tkph && formik.errors.tkph}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  name="min_tread_depth"
                  label={t('tirePerformance.minTreadDepth', 'Min Tread Depth (mm)') + ' *'}
                  type="number"
                  value={formik.values.min_tread_depth}
                  onChange={formik.handleChange}
                  error={formik.touched.min_tread_depth && !!formik.errors.min_tread_depth}
                  helperText={formik.touched.min_tread_depth && formik.errors.min_tread_depth}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  name="expected_lifetime_km"
                  label={t('specification.expectedLifetimeKm', 'Expected Lifetime (KM)')}
                  type="number"
                  value={formik.values.expected_lifetime_km}
                  onChange={formik.handleChange}
                  error={formik.touched.expected_lifetime_km && !!formik.errors.expected_lifetime_km}
                  helperText={formik.touched.expected_lifetime_km && formik.errors.expected_lifetime_km}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  name="expected_lifetime_hour"
                  label={t('specification.expectedLifetimeHour', 'Expected Lifetime (Hour)')}
                  type="number"
                  value={formik.values.expected_lifetime_hour}
                  onChange={formik.handleChange}
                  error={formik.touched.expected_lifetime_hour && !!formik.errors.expected_lifetime_hour}
                  helperText={formik.touched.expected_lifetime_hour && formik.errors.expected_lifetime_hour}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  name="max_speed"
                  label={t('specification.maxSpeed', 'Max Speed')}
                  type="number"
                  value={formik.values.max_speed}
                  onChange={formik.handleChange}
                  error={formik.touched.max_speed && !!formik.errors.max_speed}
                  helperText={formik.touched.max_speed && formik.errors.max_speed}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  select
                  name="status"
                  label={t('masterCompany.status', 'Status')}
                  value={formik.values.status}
                  onChange={formik.handleChange}
                  error={formik.touched.status && !!formik.errors.status}
                  helperText={formik.touched.status && formik.errors.status}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </form>
        )}
      </Box>
    </FormDialog>
  );
};

export default SpecificationAddEdit;
